import { Box, Button, Grid, Group, Tabs } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy } from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import FacebookInitialSettings from '../../components/Facebook/FacebookInitialSettings';
import FacebookTrackingSettings from '../../components/Facebook/FacebookTrackingSettings';
import WhatsAppSettings from '../../components/Facebook/WhatsAppSettings';
import Metadata from '../../components/Metadata';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import isInvalidCronExpression from '../../helpers/cronValidator';

import { Page, SettingsDomainTabs } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

import Spinner from '../../components/Spinner';
import defaultFacebookAccount from '../../constants/defaultFacebookAccount';

export const mergeCatalogCleanupResult = (
  account,
  { lastCatalogCleanupAt, lastCatalogCleanupVerdict, lastCatalogCleanupSummary }
) => ({
  ...account,
  lastCatalogCleanupAt,
  lastCatalogCleanupVerdict,
  lastCatalogCleanupSummary,
});

const SettingsMeta = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId, domainId } = useParams();

  const user = useSelector((state) => state.auth.user);
  const isSuperAdmin = user.userRoles.some((role) => role.role === 'superAdmin');

  const [error, setError] = useState(false);
  const [facebookAccount, setFacebookAccount] = useState(defaultFacebookAccount);
  const [previousFacebookAccount, setPreviousFacebookAccount] = useState(defaultFacebookAccount);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [terms, setTerms] = useState({
    accepted: false,
    valueBasedAccepted: false,
  });
  const [termsLoading, setTermsLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const search = queryString.parse(location.search);
  let tab = search.tab || 'facebook';

  const setTab = (tab) => {
    navigate(location.pathname.replace(`/settings/meta`, `/settings/meta?tab=${tab}`));
  };

  const toggleConfirmationModal = () => {
    setShowConfirmationModal(!showConfirmationModal);
  };

  const facebookAccountChanged =
    previousFacebookAccount !== null && JSON.stringify(facebookAccount) !== JSON.stringify(previousFacebookAccount);

  const facebookAdAccountChanged =
    previousFacebookAccount !== null &&
    previousFacebookAccount?.adAccountId && // Only if there was a previous ad account
    JSON.stringify(facebookAccount?.adAccountId) !== JSON.stringify(previousFacebookAccount?.adAccountId);

  const facebookAdAccountChangedToNone =
    previousFacebookAccount !== null && !!previousFacebookAccount?.adAccountId && !facebookAccount?.adAccountId;

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/account`)
      .then(({ facebookAccount }) => {
        setFacebookAccount(facebookAccount);
        setPreviousFacebookAccount(facebookAccount || defaultFacebookAccount);
      })
      .catch(setError);
  }, [fetch, accountId, domainId]);

  useEffect(() => {
    setTerms({ accepted: false, valueBasedAccepted: false });

    if (facebookAccount?.adAccountId) {
      setTermsLoading(true);
      fetch(
        `/api/admin/accounts/${accountId}/domains/${domainId}/facebook/customaudiences/tos?adAccountId=${facebookAccount.adAccountId}`
      )
        .then(({ accepted, valueBasedAccepted }) => {
          setTerms({ accepted, valueBasedAccepted });
        })
        .catch(setError)
        .finally(() => setTermsLoading(false));
    }
  }, [fetch, accountId, domainId, facebookAccount?.adAccountId]);

  const isValidFacebookSettings =
    facebookAccount?.adAccountId &&
    facebookAccount?.pageId &&
    facebookAccount?.pixelId &&
    facebookAccount?.productSyncSchedule &&
    !isInvalidCronExpression(facebookAccount.productSyncSchedule) &&
    facebookAccount?.audienceSyncSchedule &&
    !isInvalidCronExpression(facebookAccount.audienceSyncSchedule) &&
    facebookAccount?.countries?.length;

  // The Tracking sub-tab only requires an ad account; it has no extra required fields,
  // so tracking changes can be saved without the whole Connection form being valid.
  const isValidTrackingSettings = !!facebookAccount?.adAccountId;

  const isCurrentSectionValid = tab === 'tracking' ? isValidTrackingSettings : isValidFacebookSettings;

  const handleChange = (event) => {
    const { name, value } = event.target;

    const resetFacebookAccountFields = () => {
      facebookAccount.adAccountId = '';
      facebookAccount.appId = '';
      facebookAccount.catalogId = '';
      facebookAccount.countries = [];
      facebookAccount.pixelId = '';
      facebookAccount.pageId = '';
      facebookAccount.productSyncSchedule = '';
      facebookAccount.audienceSyncSchedule = '';
      facebookAccount.termsAccepted = false;
      facebookAccount.backendTracking = false;
      facebookAccount.advantagePlusCreativeEnabled = true;
      facebookAccount.enabledTrackingEvents = [];
    };

    switch (name) {
      case 'businessId':
        resetFacebookAccountFields();
        break;
      case 'adAccountId':
        if (!value) resetFacebookAccountFields();
        break;
      default:
        break;
    }
    setFacebookAccount((prevState) => ({ ...prevState, [name]: value }));
  };

  const submitForm = () => {
    if (!facebookAccount || !facebookAccount.businessId) {
      return;
    }

    setError(undefined);
    setSaveLoading(true);

    // Don't persist tracked events when server-side tracking (CAPI) is off.
    const payload = {
      ...facebookAccount,
      enabledTrackingEvents: facebookAccount.backendTracking ? facebookAccount.enabledTrackingEvents : [],
    };

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/account`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
      .then(({ facebookAccount }) => {
        setFacebookAccount(facebookAccount);
        setPreviousFacebookAccount(facebookAccount);
      })
      .catch(setError)
      .finally(() => setSaveLoading(false));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const form = event?.currentTarget;
    const isFormValid = form?.checkValidity();

    if (!isFormValid) {
      event.stopPropagation();
      return;
    }

    if ((tab === 'facebook' || tab === 'tracking') && isCurrentSectionValid) {
      if (facebookAdAccountChanged || showConfirmationModal) {
        toggleConfirmationModal();
      } else {
        submitForm();
      }
    }
  };

  const isSaveButtonEnabled = facebookAccountChanged && isCurrentSectionValid;

  const submitButton = () => {
    return saveLoading ? (
      <Spinner />
    ) : (
      <Button type="button" disabled={!isSaveButtonEnabled} onClick={handleSubmit}>
        <span>
          <IconDeviceFloppy size={14} /> {facebookAccountChanged ? 'Save' : 'Saved'}
        </span>
      </Button>
    );
  };

  const cancel = () => {
    navigate(`/accounts/${accountId}/domains/${domainId}/settings/meta?tab=${tab}`);
    window.location.reload();
  };

  let modalMessage = '';

  if (facebookAdAccountChanged) {
    modalMessage = facebookAdAccountChangedToNone
      ? 'Please note that any active campaigns will be paused in Facebook Ads Manager and permanently stopped in Releva due to disabling of Facebook Ads.'
      : 'Please note that modifying your Facebook Ad Account will trigger the creation of new Facebook Ads reflecting the revised settings.';
  }

  const applyCatalogCleanupResult = (refreshedAccount) => {
    setFacebookAccount((prevState) => mergeCatalogCleanupResult(prevState, refreshedAccount));
    setPreviousFacebookAccount((prevState) => mergeCatalogCleanupResult(prevState, refreshedAccount));
  };

  const syncProductsNow = () => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/syncFacebookCatalog`, {
      method: 'POST',
    })
      .then(() => {
        notifications.show({
          title: 'Sync Scheduled',
          message: 'Sync has been scheduled successfully.',
          color: 'success',
        });
      })
      .catch((e) => {
        notifications.show({
          title: 'Catalog Sync Error',
          message: `We were unable to schedule the catalog sync due to the following error: ${e.message || e}.`,
          color: 'danger',
        });
      });
  };

  const syncAudiencesNow = () => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/syncAudiences`, {
      method: 'POST',
    })
      .then(() => {
        notifications.show({
          title: 'Sync Scheduled',
          message: 'Sync has been scheduled successfully.',
          color: 'success',
        });
      })
      .catch((e) => {
        notifications.show({
          title: 'Catalog Sync Error',
          message: `We were unable to schedule the catalog sync due to the following error: ${e.message || e}.`,
          color: 'danger',
        });
      });
  };

  return (
    <div>
      <Metadata title={`${Page['meta']} | ${SettingsDomainTabs[tab]}`} />
      <Grid pt={30} my={20}>
        <Grid.Col span={12}>
          <Box component="form" ta="left" noValidate onSubmit={(e) => handleSubmit(e)}>
            <Tabs value={tab} onChange={setTab} mb="md" keepMounted>
              <Tabs.List>
                <Tabs.Tab value="facebook" data-tour="shopSettingsFacebookTab">
                  Facebook
                </Tabs.Tab>
                {facebookAccount?.adAccountId && <Tabs.Tab value="tracking">Tracking</Tabs.Tab>}
                <Tabs.Tab value="whatsApp">WhatsApp</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="facebook">
                <FacebookInitialSettings
                  facebookAccount={facebookAccount}
                  setFacebookAccount={setFacebookAccount}
                  handleChange={handleChange}
                  setTab={setTab}
                  terms={terms}
                  termsLoading={termsLoading}
                  syncProductsNow={syncProductsNow}
                  syncAudiencesNow={syncAudiencesNow}
                  isSuperAdmin={isSuperAdmin}
                  onFacebookAccountChange={applyCatalogCleanupResult}
                />
              </Tabs.Panel>
              {facebookAccount?.adAccountId && (
                <Tabs.Panel value="tracking" pt="md">
                  <FacebookTrackingSettings facebookAccount={facebookAccount} handleChange={handleChange} />
                </Tabs.Panel>
              )}
              <Tabs.Panel value="whatsApp">
                <WhatsAppSettings />
              </Tabs.Panel>
            </Tabs>
            <Group justify="flex-end" gap="xs" py="md">
              {error && <ErrorMessage message={error.message} />}
              {(tab === 'facebook' || tab === 'tracking') && facebookAccountChanged && (
                <Button variant="default" onClick={() => cancel()}>
                  Cancel
                </Button>
              )}

              {(tab === 'facebook' || tab === 'tracking') && facebookAccount && submitButton()}
            </Group>
            {showConfirmationModal && (
              <ConfirmationModal
                show={showConfirmationModal}
                close={(cb) => toggleConfirmationModal(cb)}
                confirm={(cb) => submitForm(cb)}
                message={modalMessage}
              />
            )}
          </Box>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default SettingsMeta;
